import React, { useEffect, useRef } from 'react'
import Chart, { TooltipItem } from 'chart.js/auto'
import { calculateAvgLast10Time, formatApiName } from './ChessApiAnalytics'
import type { ChessApiMetric } from './ChessApi'

interface ChessApiChartProps {
  data: Record<string, ChessApiMetric>
}

const ChessApiChart = ({ data }: ChessApiChartProps) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const chartRef = useRef<Chart | null>(null)

  const transformData = (input: Record<string, ChessApiMetric>) => {
    const apiNames = Object.keys(input).map(formatApiName)
    const values = Object.values(input)

    const avgExecutionTimes = apiNames.map((name, index) => ({
      name,
      value: Number(calculateAvgLast10Time(values[index].last10Time))
    }))

    const maxExecutionTimes = apiNames.map((name, index) => ({
      name,
      value: values[index].maxTime
    }))

    const totalCalls = apiNames.map((name, index) => ({
      name,
      value: values[index].count
    }))

    return { apiNames, avgExecutionTimes, maxExecutionTimes, totalCalls }
  }

  const { apiNames, avgExecutionTimes, maxExecutionTimes, totalCalls } = transformData(data)

  const normalizedData = {
    labels: apiNames,
    datasets: [
      {
        label: 'Avg Execution Time (last 10 calls)',
        data: avgExecutionTimes.map(item => item.value ?? 0),
        backgroundColor: 'rgba(75, 192, 192, 1)'
      },
      {
        label: 'Max Execution Time (reset every month)',
        data: maxExecutionTimes.map(item => item.value ?? 0),
        backgroundColor: 'rgba(153, 102, 255, 1)'
      },
      {
        label: 'Total Number of Calls (reset every month)',
        data: totalCalls.map(item => item.value ?? 0),
        backgroundColor: 'rgba(255, 159, 64, 1)'
      }
    ]
  }

  const options = {
    maintainAspectRatio: false,
    plugins: {
      tooltip: {
        callbacks: {
          label: function (context: TooltipItem<'bar'>) {
            let label = context.dataset.label || ''
            if (label) {
              label += ': '
            }
            if (context.dataset.label === 'Avg Execution Time (last 10 calls)') {
              label += avgExecutionTimes[context.dataIndex].value
            } else if (context.dataset.label === 'Max Execution Time (reset every month)') {
              label += maxExecutionTimes[context.dataIndex].value
            } else if (context.dataset.label === 'Total Number of Calls (reset every month)') {
              label += totalCalls[context.dataIndex].value
            }
            return label
          }
        }
      }
    },
    scales: {
      x: {
        display: false
      },
      y: {
        type: 'logarithmic' as const,
        display: false
      }
    },
    categoryPercentage: 0.8,
    barPercentage: 1
  }

  useEffect(() => {
    if (!canvasRef.current) return

    if (chartRef.current) {
      chartRef.current.destroy()
      chartRef.current = null
    }

    chartRef.current = new Chart(canvasRef.current, {
      type: 'bar',
      data: normalizedData,
      options
    })

    return () => {
      if (chartRef.current) {
        chartRef.current.destroy()
        chartRef.current = null
      }
    }
  }, [data])

  return (
    <div className="container mt-5">
      <div className="chart-container">
        <canvas ref={canvasRef} />
      </div>
    </div>
  )
}

export default ChessApiChart
